import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core'
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms'
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog'
import { BehaviorSubject, Observable } from 'rxjs'
import { filter, map, switchMap, tap } from 'rxjs/operators'
import { SubSink } from 'subsink'

import { ITaxCode } from '../../models/itax-code.model'
import { TaxCodeService } from '../../services/tax-code.service'

@Component({
  selector: 'tc-tax-code-lookup',
  templateUrl: './tax-code-lookup.dialog.html',
  styleUrls: ['./tax-code-lookup.dialog.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaxCodeLookupDialog implements OnInit, OnDestroy {
  sub = new SubSink()

  childOptions$: BehaviorSubject<ITaxCode[]> = new BehaviorSubject([])
  grandchildOptions$: BehaviorSubject<ITaxCode[]> = new BehaviorSubject([])
  selected$: BehaviorSubject<ITaxCode> = new BehaviorSubject(null)

  form: UntypedFormGroup
  parent = new UntypedFormControl()
  child = new UntypedFormControl()
  grandchild = new UntypedFormControl()

  constructor(
    public dialogRef: MatDialogRef<TaxCodeLookupDialog>,
    private taxCodeService: TaxCodeService,
    private fb: UntypedFormBuilder
  ) {
    this.form = fb.group({
      parent: this.parent,
      child: this.child,
      grandchild: this.grandchild,
    })
  }

  parentOptions$: Observable<ITaxCode[]> = this.taxCodeService.getTopTaxCodes()

  ngOnInit(): void {
    this.sub.add(
      this.form.valueChanges
        .pipe(
          map(({ parent, child, grandchild }) => {
            if (grandchild) {
              return grandchild
            }
            if (child) {
              return child
            }
            return parent
          }),
          filter((item) => !!item),
          switchMap((ticId: number) => {
            return this.taxCodeService.getTaxCode(ticId)
          }),
          tap((selected) => this.selected$.next(selected))
        )
        .subscribe()
    )

    this.sub.add(
      this.parent.valueChanges
        .pipe(
          switchMap((parentId) => {
            return this.taxCodeService.getChildren(parentId)
          }),
          tap((children) => this.childOptions$.next(children)),
          tap(() => this.child.setValue(null)),
          tap(() => this.grandchild.setValue(null)),
          tap(() => this.grandchildOptions$.next([]))
        )
        .subscribe()
    )

    this.sub.add(
      this.child.valueChanges
        .pipe(
          switchMap((ticId) => {
            return this.taxCodeService.getChildren(ticId)
          }),
          tap(() => this.grandchild.setValue(null)),
          tap((children) => this.grandchildOptions$.next(children))
        )
        .subscribe()
    )
  }
  ngOnDestroy(): void {
    this.sub.unsubscribe()
  }
  cancel(): void {
    this.dialogRef.close({ result: null })
  }

  confirm(): void {
    this.dialogRef.close({ result: this.selected$.value })
  }
}
